import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, FileText, Sparkles, Loader2, Scan } from 'lucide-react';
import { usePartnerSettings } from '@/hooks/usePartnerSettings';
import { useContractorProfile } from '@/hooks/useContractorProfile';
import { useDocumentExtraction, type ExtractedDocumentData } from '@/hooks/useDocumentExtraction';
import { useMultiTypeDetection, type DetectedPolicy } from '@/hooks/useMultiTypeDetection';
import { DocumentExtractionConfirmDialog } from '@/components/partners/DocumentExtractionConfirmDialog';
import { MultiTypeDetectionDialog } from '@/components/partners/MultiTypeDetectionDialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface PortalDocumentUploadProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  preSelectedDocumentType?: string | null;
  onSuccess?: () => void;
}

export function PortalDocumentUpload({ 
  open: controlledOpen, 
  onOpenChange, 
  preSelectedDocumentType,
  onSuccess 
}: PortalDocumentUploadProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [extractedData, setExtractedData] = useState<ExtractedDocumentData | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [detectedPolicies, setDetectedPolicies] = useState<DetectedPolicy[]>([]);
  const [showMultiTypeDialog, setShowMultiTypeDialog] = useState(false);
  const [useManualEntry, setUseManualEntry] = useState(false);
  
  const { profile } = useContractorProfile();
  const { settings } = usePartnerSettings();
  const { extractDocument, isExtracting } = useDocumentExtraction();
  const { detectDocumentTypes, isDetecting } = useMultiTypeDetection();

  const documentTypes = settings?.document_types || [];
  const requiredTypes = documentTypes.filter((dt: any) => dt.required_for_prequalification);
  
  // Determine if selected document type requires expiry date
  const selectedDocType = documentTypes.find((dt: any) => dt.name === documentType);
  const showDateFields = selectedDocType?.has_expiry ?? true;

  // Use controlled or internal open state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  // Pre-select document type when provided
  useEffect(() => {
    if (preSelectedDocumentType && open) {
      setDocumentType(preSelectedDocumentType);
      setUseManualEntry(true);
    }
  }, [preSelectedDocumentType, open]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a PDF or image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setSelectedFile(file);
    setUseManualEntry(false);
  };

  const handleAnalyzeDocument = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    const result = await detectDocumentTypes(selectedFile, documentTypes);
    if (result && result.detected_policies.length > 0) {
      if (result.detected_policies.length === 1) {
        // Single policy: auto-fill the form
        const policy = result.detected_policies[0];
        setDocumentType(policy.document_type_id);
        if (policy.issue_date) setIssueDate(policy.issue_date);
        if (policy.expiry_date) setExpiryDate(policy.expiry_date);
        setExtractedData({
          document_number: policy.document_number,
          issuing_authority: policy.issuing_authority,
          issue_date: policy.issue_date,
          expiry_date: policy.expiry_date,
          confidence: policy.confidence,
          holder_name: policy.holder_name,
          coverage_amount: policy.coverage_amount,
          additional_info: policy.additional_info,
        } as ExtractedDocumentData);
      } else {
        // Multiple policies: show selection dialog
        setDetectedPolicies(result.detected_policies);
        setShowMultiTypeDialog(true);
      }
    } else {
      // Fallback to manual entry
      setUseManualEntry(true);
      toast.info('Please select the document type manually');
    }
  };

  const handleExtractFields = async () => {
    if (!selectedFile || !documentType) {
      toast.error('Please select a file and document type first');
      return;
    }

    const data = await extractDocument(selectedFile, documentType);
    if (data) {
      setExtractedData(data);
      setShowConfirmDialog(true);
    }
  };

  const handleConfirmExtraction = async (confirmedData: ExtractedDocumentData) => {
    if (confirmedData.issue_date) setIssueDate(confirmedData.issue_date);
    if (confirmedData.expiry_date) setExpiryDate(confirmedData.expiry_date);
    
    setShowConfirmDialog(false);
    await handleUploadWithData(confirmedData);
  };

  const handleManualEntry = () => {
    setShowConfirmDialog(false);
    setExtractedData(null);
  };

  const handleUploadWithData = async (data?: ExtractedDocumentData) => {
    if (!selectedFile || !documentType || !profile) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (showDateFields && expiryDate && new Date(expiryDate) <= new Date(issueDate || new Date())) {
      toast.error('Expiry date must be after issue date');
      return;
    }

    setUploading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Upload file to storage
      const timestamp = Date.now();
      const filePath = `${profile.tenant_id}/${profile.company_id}/${documentType}/${timestamp}-${selectedFile.name}`;

      const { error: uploadError } = await supabase.storage
        .from('partner-documents')
        .upload(filePath, selectedFile);

      if (uploadError) throw uploadError;

      // Create document record
      const insertData: any = {
        company_id: profile.company_id,
        tenant_id: profile.tenant_id,
        document_type: documentType,
        document_category: 'insurance',
        file_path: filePath,
        file_name: selectedFile.name,
        file_size_bytes: selectedFile.size,
        mime_type: selectedFile.type,
        is_required_for_prequalification: true,
        uploaded_by_contractor_user_id: profile.id,
        verification_status: 'pending',
      };

      if (data?.document_number) insertData.document_number = data.document_number;
      if (data?.issuing_authority) insertData.issuing_authority = data.issuing_authority;
      if (data?.issue_date || issueDate) insertData.issue_date = data?.issue_date || issueDate;
      if (data?.expiry_date || expiryDate) insertData.expiry_date = data?.expiry_date || expiryDate;
      if (data) {
        insertData.extracted_data = {
          ...data,
          coverage_type: data.coverage_type,
          coverage_amount: data.coverage_amount,
          holder_name: data.holder_name,
          additional_info: data.additional_info
        };
        insertData.extraction_confidence = data.confidence;
      }

      const { error: insertError } = await supabase
        .from('partner_documents')
        .insert(insertData);

      if (insertError) throw insertError;

      toast.success('Document uploaded successfully');
      setOpen(false);
      setSelectedFile(null);
      setDocumentType('');
      setIssueDate('');
      setExpiryDate('');
      setExtractedData(null);
      onSuccess?.();
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async () => {
    await handleUploadWithData();
  };

  const handleConfirmMultiUpload = async (selectedPolicies: DetectedPolicy[]) => {
    if (!selectedFile || !profile) return;

    setUploading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      console.log('Starting multi-upload:', {
        policyCount: selectedPolicies.length,
        fileName: selectedFile.name,
        companyId: profile.company_id,
        tenantId: profile.tenant_id
      });

      // Validate policies before upload
      const invalidPolicies = selectedPolicies.filter(p => 
        !p.document_type_id || !p.document_type_name
      );
      
      if (invalidPolicies.length > 0) {
        console.error('Invalid policies detected:', invalidPolicies);
        throw new Error(`${invalidPolicies.length} policies missing required fields`);
      }

      // Upload file once with detailed error handling
      const timestamp = Date.now();
      const filePath = `${profile.tenant_id}/${profile.company_id}/${timestamp}-${selectedFile.name}`;

      console.log('Uploading file to storage:', filePath);
      const { error: uploadError, data: uploadData } = await supabase.storage
        .from('partner-documents')
        .upload(filePath, selectedFile);

      if (uploadError) {
        console.error('Storage upload error:', uploadError);
        throw new Error(`Storage upload failed: ${uploadError.message}`);
      }
      
      console.log('File uploaded successfully:', uploadData);

      // Create records for each selected policy
      const records = selectedPolicies.map((policy, idx) => {
        const record = {
          company_id: profile.company_id,
          tenant_id: profile.tenant_id,
          document_type: policy.document_type_name,
          document_category: 'insurance',
          file_path: filePath,
          file_name: selectedFile.name,
          file_size_bytes: selectedFile.size,
          mime_type: selectedFile.type,
          is_required_for_prequalification: true,
          uploaded_by_contractor_user_id: profile.id,
          verification_status: 'pending',
          document_number: policy.document_number || null,
          issue_date: policy.issue_date || null,
          expiry_date: policy.expiry_date || null,
          issuing_authority: policy.issuing_authority || null,
          extracted_data: policy.additional_info ? {
            ...policy.additional_info,
            coverage_amount: policy.coverage_amount,
            holder_name: policy.holder_name,
          } : null,
          extraction_confidence: policy.confidence || 0,
        };
        
        console.log(`Record ${idx + 1}:`, record);
        return record;
      });

      console.log('Inserting records to database:', records.length);
      const { error: insertError, data: insertData } = await supabase
        .from('partner_documents')
        .insert(records)
        .select();

      if (insertError) {
        console.error('Database insert error:', {
          message: insertError.message,
          details: insertError.details,
          hint: insertError.hint,
          code: insertError.code
        });
        
        // Cleanup: Delete uploaded file if database insert fails
        await supabase.storage
          .from('partner-documents')
          .remove([filePath]);
        
        throw new Error(`Database insert failed: ${insertError.message}`);
      }

      console.log('Successfully inserted records:', insertData);
      toast.success(`Created ${selectedPolicies.length} documents from certificate`);
      
      // Reset state and close dialog
      setOpen(false);
      setSelectedFile(null);
      setDocumentType('');
      setIssueDate('');
      setExpiryDate('');
      setExtractedData(null);
      setDetectedPolicies([]);
      setShowMultiTypeDialog(false);
      onSuccess?.();
      
    } catch (error: any) {
      console.error('Multi-upload error:', {
        message: error.message,
        error: error,
        stack: error.stack
      });
      
      toast.error('Failed to upload documents', {
        description: error.message || 'Please check the console for details'
      });
    } finally {
      setUploading(false);
    }
  };

  if (!profile?.can_upload_documents && !profile?.is_primary_contact) {
    return null;
  }

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button size="lg" className="gap-2">
            <Upload className="h-4 w-4" />
            Upload Document
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="file">File *</Label>
              <Input
                id="file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileSelect}
                disabled={uploading || isDetecting}
              />
              {selectedFile && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <FileText className="h-4 w-4" />
                  <span>{selectedFile.name}</span>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                PDF or image files, max 10MB
              </p>
            </div>

            {selectedFile && !useManualEntry && !extractedData && (
              <Button
                type="button"
                variant="default"
                onClick={handleAnalyzeDocument}
                disabled={isDetecting || uploading}
                className="w-full"
              >
                {isDetecting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Analyzing document...
                  </>
                ) : (
                  <>
                    <Scan className="mr-2 h-4 w-4" />
                    Extract with AI
                  </>
                )}
              </Button>
            )}

            {(useManualEntry || extractedData) && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="document-type">Document Type *</Label>
                  <Select value={documentType} onValueChange={setDocumentType}>
                    <SelectTrigger id="document-type">
                      <SelectValue placeholder="Select document type" />
                    </SelectTrigger>
                    <SelectContent>
                      {requiredTypes.length > 0 ? (
                        requiredTypes.map((type: any) => (
                          <SelectItem key={type.name} value={type.name}>
                            <div className="flex items-center gap-2">
                              {type.name}
                              {type.required_for_prequalification && (
                                <span className="text-xs text-red-500">Required</span>
                              )}
                            </div>
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="other">Other Document</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {selectedFile && documentType && !extractedData && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleExtractFields}
                      disabled={isExtracting || uploading}
                      className="w-full"
                    >
                      {isExtracting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Analyzing document...
                        </>
                      ) : (
                        <>
                          <Sparkles className="mr-2 h-4 w-4" />
                          Extract Fields with AI
                        </>
                      )}
                    </Button>
                  </>
                )}
              </>
            )}

            {showDateFields && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="issue-date">Issue Date</Label>
                  <Input
                    id="issue-date"
                    type="date"
                    value={issueDate}
                    onChange={(e) => setIssueDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expiry-date">Expiry Date</Label>
                  <Input
                    id="expiry-date"
                    type="date"
                    value={expiryDate}
                    onChange={(e) => setExpiryDate(e.target.value)}
                  />
                </div>
              </div>
            )}

            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-3">
              <p className="text-xs text-blue-800 dark:text-blue-200">
                💡 Your document will be reviewed by the project team. You'll be notified once it's verified.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpload} 
              disabled={uploading || !selectedFile || !documentType || isExtracting || isDetecting}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {extractedData && (
        <DocumentExtractionConfirmDialog
          open={showConfirmDialog}
          onOpenChange={setShowConfirmDialog}
          extractedData={extractedData}
          onConfirm={handleConfirmExtraction}
          onManualEntry={handleManualEntry}
          isSubmitting={uploading}
          showDateFields={showDateFields}
        />
      )}

      {detectedPolicies.length > 0 && (
        <MultiTypeDetectionDialog
          open={showMultiTypeDialog}
          onOpenChange={setShowMultiTypeDialog}
          detectedPolicies={detectedPolicies}
          onConfirm={handleConfirmMultiUpload}
          isSubmitting={uploading}
        />
      )}
    </>
  );
}
